import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import { ChartData, Point } from "chart.js";
import "chart.js/auto";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { OutcomeSummary, OutcomesForParty } from "../../shared/Analytics";
import { PartyDescriptors } from "../../shared/CaseModel";
import { percentage } from "../../shared/percentage";
import { getTooltip } from "../../utils/chartjsTheming";
import { Metric } from "../general/dataAtoms/Metric";
import { NO_VALUE } from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { getMaxAbsoluteValue } from "./ChartsUtility";
import {
  RISK_CHART_HEIGHT,
  RISK_LINE_POINTS,
  riskChartLegend,
  riskChartScales,
} from "./riskChart";
import { buildRiskSeries } from "./riskSeries";

export const varianceFormatter = new Intl.NumberFormat("en-GB", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 3,
});

/**
 * How far the series of a party's risk are lightened from the primary colour:
 * the enforcement result as an area with its edge, the net result as the
 * darker line over it.
 */
const ENFORCEMENT_AREA_LIGHTEN = 0.6;
const ENFORCEMENT_EDGE_LIGHTEN = 0.4;
const NET_RESULT_LIGHTEN = 0.2;

/** How much of its colour the area of the enforcement result takes. */
const ENFORCEMENT_AREA_ALPHA = 0.45;

/** The room the scale leaves over the highest and under the lowest amount. */
const SCALE_HEADROOM = 1.1;

export const OutcomeRiskAnalysis = ({
  outcome,
  parties = {},
  showAverageEV = false,
}: {
  outcome: OutcomesForParty<OutcomeSummary>;
  parties?: PartyDescriptors;
  showAverageEV?: boolean;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "case.analytics.risk" });
  const party = parties[outcome.partyId];
  const { main: primary } = theme.palette.primary;

  const {
    sortedJudgments,
    judgmentEvs,
    netResults,
    averageEnforcement,
    bestEnforcement,
    worstEnforcement,
    negativeOutcomes,
    negativeOutcomesProbability,
    assetLimitReached,
    assetLimitReachedProbability,
  } = useMemo(
    () => buildRiskSeries(outcome, party?.assetLimit),
    [outcome, party?.assetLimit],
  );

  if (sortedJudgments.length === 0)
    return (
      <Typography sx={{ color: "text.secondary" }}>
        {t("description", { count: 0 })}
      </Typography>
    );

  const chartData: ChartData<"line", (number | Point)[]> = {
    datasets: [
      {
        label: t("chart.enforcementResult"),
        data: judgmentEvs,
        stepped: true,
        fill: true,
        order: 2,
        backgroundColor: alpha(
          lighten(primary, ENFORCEMENT_AREA_LIGHTEN),
          ENFORCEMENT_AREA_ALPHA,
        ),
        borderColor: lighten(primary, ENFORCEMENT_EDGE_LIGHTEN),
        borderWidth: 2,
        ...RISK_LINE_POINTS,
      },
      {
        label: t("chart.netResult"),
        data: netResults,
        stepped: true,
        fill: false,
        order: 1,
        // a line has no area: its box in the legend stays empty
        backgroundColor: "transparent",
        borderColor: lighten(primary, NET_RESULT_LIGHTEN),
        borderWidth: 2.5,
        borderDash: [7, 5],
        ...RISK_LINE_POINTS,
      },
    ],
  };

  if (showAverageEV)
    chartData.datasets.push({
      label: t("chart.caseValue"),
      data: [{ x: 0.5, y: averageEnforcement }],
      order: 1,
      pointStyle: "circle",
      pointRadius: 6,
      pointHoverRadius: 10,
      pointHitRadius: 20,
      backgroundColor: theme.palette.primary.light,
      borderColor: theme.palette.primary.dark,
    });

  let maxChartValue = getMaxAbsoluteValue(chartData);
  // if there is an asset value and it is less than double the highest value, show it
  if (
    party?.assetLimit !== undefined &&
    party?.assetLimit !== "unlimited" &&
    party.assetLimit < maxChartValue * 2
  ) {
    chartData.datasets.push({
      label: t("chart.assetLimit"),
      data: [
        { x: 0, y: -party.assetLimit },
        { x: 1, y: -party.assetLimit },
      ],
      order: 0,
      backgroundColor: "transparent",
      borderColor: theme.palette.data.negative.main,
      borderWidth: 2,
      ...RISK_LINE_POINTS,
    });
    // make sure to scale in case asset value is higher
    maxChartValue = getMaxAbsoluteValue(chartData);
  }

  const name = party?.name ?? outcome.partyId;
  const metrics: { label: string; value: string; isAdverse?: boolean }[] = [
    { label: t("summary.total"), value: String(sortedJudgments.length) },
    { label: t("summary.negative"), value: String(negativeOutcomes) },
    {
      label: t("summary.negativeProbability"),
      value: percentage.format(negativeOutcomesProbability),
      // the one figure of the five that is a risk to the party's money
      isAdverse: negativeOutcomesProbability > 0,
    },
    {
      label: t("summary.worst"),
      value:
        worstEnforcement === undefined
          ? NO_VALUE
          : currency.format(worstEnforcement),
    },
    {
      label: t("summary.best"),
      value:
        bestEnforcement === undefined
          ? NO_VALUE
          : currency.format(bestEnforcement),
    },
  ];

  return (
    <Stack spacing={2.25}>
      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2.5 }}
      >
        <SectionLabel component="h2">
          {t("description", { count: sortedJudgments.length, name })}
        </SectionLabel>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            columnGap: 3.5,
            rowGap: 2.25,
            mt: 2,
          }}
        >
          {metrics.map(({ label, value, isAdverse }) => (
            <Metric
              key={label}
              label={label}
              value={value}
              tone={isAdverse ? "adverse" : undefined}
            />
          ))}
        </Box>
        {assetLimitReached > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t("warning.assetLimit", {
              name,
              count: assetLimitReached,
              probability: percentage.format(assetLimitReachedProbability),
            })}
          </Alert>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2 }}>
        <SectionLabel component="h2">{t("title")}</SectionLabel>
        <Box sx={{ height: RISK_CHART_HEIGHT }}>
          <Line
            data={chartData}
            options={{
              maintainAspectRatio: false,
              plugins: {
                legend: riskChartLegend(theme),
                tooltip: {
                  ...getTooltip(theme),
                  mode: "index",
                  intersect: true,
                  callbacks: {
                    title: () => "",
                    label: (item) =>
                      `${item.dataset.label}: ${currency.format(item.parsed.y ?? 0)}`,
                  },
                },
              },
              scales: riskChartScales({
                theme,
                currency,
                axisTitle: t("chart.axis"),
                // zero stays in the middle, so a loss reads as far down as a
                // gain of its size reads up
                y: {
                  min: -SCALE_HEADROOM * maxChartValue,
                  max: SCALE_HEADROOM * maxChartValue,
                },
              }),
            }}
          />
        </Box>
      </Paper>
    </Stack>
  );
};
